import React, { useEffect, Fragment, useState, useRef } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useFieldArray, useForm, useWatch } from "react-hook-form";
import { showToast } from "../../../../components/ToastifyNotification";
import { UncontrolledTooltip } from "reactstrap";
import Breadcrumb from "../../../../components/common/breadcrumb";
import { IMAGE_URL } from "../../../../utils/api-config";
import { GetIpdEntries } from "../../../../api/ipd/ipdEntryAPI";
import { GetPatientHistoryById, UpdatePatientHistory } from "../../../../api/ipd/patientHistoryAPI";
import { GetMlcById, UpdateMlc } from "../../../../api/ipd/mlcAPI";
import useKeyboardSubmit from "../../../../hooks/useKeyboardSubmit";
import useEnterNavigation from "../../../../hooks/useEnterNavigation";

// Validation schema using Yup
const schema = yup.object().shape({
    mlcNo: yup.string().required("Mlc No is required"),
    ipdEntryId: yup.string().required("IPD Entry is required"),
    natureOfInjury: yup.string().nullable(),
    diagnosis: yup.string().required("History of present illness is required"),
    historyAndPhysicalExamination: yup.string().required("History of past illness is required"),
    treatment: yup.string().required("Family history is required"),
    markOfIdentification: yup.string().required("Family history is required"),
    remark: yup.string().required("Family history is required"),
    thanaName: yup.string().required("Family history is required"),
    patientBoughtBy: yup.string().required("Family history is required"),
    placeOfAccident: yup.string().required("Family history is required"),
});

const EditMlcEntry = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { id } = useParams();
    const [ipdEntries, setIpdEntries] = useState([]);
    const [patientDetails, setPatientDetails] = useState({});

    const {
        register,
        handleSubmit,
        control,
        setValue,
        formState: { errors }
    } = useForm({
        resolver: yupResolver(schema),
        defaultValues: {

        }
    });

    const fetchIpdEntries = async () => {
        try {
            const response = await GetIpdEntries();
            if (response.success) {
                setIpdEntries(response.data);
            } else {
                showToast("error", response.message);
            }
        } catch (error) {
            showToast("error", error.message);
        }
    };

    const fetchData = async () => {
        try {
            const response = await GetMlcById(id); // Make sure login function returns token
            if (response.success) {
                Object.entries(response.data).forEach(([key, value]) => {
                    // Handle main object dates
                    if (["date"].includes(key) && value) {
                        setValue(key, value.split("T")[0]);
                        return;
                    }
                    // Default
                    setValue(key, value);
                });

                setPatientDetails(response.data?.ipdEntryId || {});
                setValue('ipdEntryId', response.data?.ipdEntryId?._id || '');

            } else {
                // setError(response.message);
                showToast("error", response.message);
            }
        } catch (error) {
            // setError(error); // Handle login errors
            showToast("error", error);
        }
    };

    // Fetch existing data
    useEffect(
        () => {
            if (id) {
                fetchIpdEntries().then(() => {
                    fetchData();
                });
            }
        },
        [id, setValue]
    );

    const onSubmit = async data => {
        try {
            dispatch({ type: "LOADER_START" });

            const response = await UpdateMlc(id, data);
            if (response.success) {
                showToast("success", response.message);
            } else {
                showToast("error", response.message);
            }
        } catch (error) {
            showToast("error", error);
        } finally {
            dispatch({ type: "LOADER_STOP" });
        }
    };

    const formRef = useRef(null);
      useKeyboardSubmit(handleSubmit, onSubmit);
      useEnterNavigation(formRef);

    return (
        <Fragment>
            <Breadcrumb parent="MLC" title="Edit MLC Entry" />
            <div className="container-fluid">
                <div className="row">
                    <div className="col-sm-12">
                        <div className="card">
                            <div className="card-header d-flex justify-content-between">
                                <h5>Edit MLC Entry</h5>
                                <Link
                                    to={`${process.env
                                        .PUBLIC_URL}/ipd/transaction/mlc-entry/list`}
                                >
                                    <button className="btn btn-primary" id="list">
                                        List MLC Entry
                                    </button>
                                    <UncontrolledTooltip placement="top" target="list">
                                        List MLC Entry
                                    </UncontrolledTooltip>
                                </Link>
                            </div>

                            <form
                                ref={formRef}
                                className="form theme-form"
                                onSubmit={handleSubmit(onSubmit)}
                            >
                                <div className="card-body">

                                    {/* ================= IPD & BASIC ================= */}
                                    <div className="row">
                                        <div className="col-6">
                                            <div className="form-group">
                                                <label>MLC No</label>
                                                <input
                                                    {...register("mlcNo")}
                                                    className="form-control"
                                                    placeholder="MLC No"
                                                    readOnly
                                                />
                                            </div>
                                        </div>
                                        <div className="col-6">
                                            <label>Room No</label>
                                            <select className="form-control" {...register("ipdEntryId", {
                                                onChange: (e) => {
                                                    setPatientDetails(ipdEntries.find(ipd => ipd._id === e.target.value));
                                                }
                                            })}>
                                                <option value="">Select IPD</option>
                                                {ipdEntries.map(ipd => (
                                                    <option key={ipd._id} value={ipd._id}>
                                                        {ipd.roomId?.roomNo}
                                                    </option>
                                                ))}
                                            </select>
                                            {errors.ipdEntryId && <span className="text-danger">{errors.ipdEntryId.message}</span>}
                                        </div>
                                    </div>

                                    <div className="col-12">
                                        <h5 className="mt-3 mb-3 text-primary">
                                            Patient Details
                                        </h5>
                                    </div>
                                    <div className="row">
                                        <div className="col-6">
                                            <div className="form-group">
                                                <label>UHID No</label>
                                                <input
                                                    className="form-control"
                                                    placeholder="UHID No"
                                                    value={patientDetails?.patientId?.uhidNo}
                                                    readOnly
                                                />
                                            </div>
                                        </div>
                                        <div className="col-6">
                                            <div className="form-group">
                                                <label>Admit Date</label>
                                                <input
                                                    className="form-control"
                                                    placeholder="Admit Date"
                                                    value={patientDetails?.date?.split("T")[0]}
                                                    readOnly
                                                />
                                            </div>
                                        </div>
                                        <div className="col-6">
                                            <div className="form-group">
                                                <label>Admit time</label>
                                                <input
                                                    className="form-control"
                                                    placeholder="Admit time"
                                                    value={patientDetails?.date?.split("T")[1].split(".")[0]}
                                                    readOnly
                                                />
                                            </div>
                                        </div>
                                        <div className="col-6">
                                            <div className="form-group">
                                                <label>Ipd No</label>
                                                <input
                                                    className="form-control"
                                                    placeholder="Ipd No"
                                                    value={patientDetails?.ipdNo}
                                                    readOnly
                                                />
                                            </div>
                                        </div>
                                        <div className="col-6">
                                            <div className="form-group">
                                                <label>Patient</label>
                                                <input
                                                    className="form-control"
                                                    placeholder="Patient Name"
                                                    value={patientDetails?.patientId?.patientName}
                                                    readOnly
                                                />
                                            </div>
                                        </div>
                                        <div className="col-6">
                                            <div className="form-group">
                                                <label>Guardian Name</label>
                                                <input
                                                    className="form-control"
                                                    placeholder="Guardian Name"
                                                    value={patientDetails?.patientId?.guardianName}
                                                    readOnly
                                                />
                                            </div>
                                        </div>
                                        <div className="col-6">
                                            <div className="form-group">
                                                <label>Age</label>
                                                <input
                                                    className="form-control"
                                                    placeholder="Patient Name"
                                                    value={patientDetails?.patientId?.age}
                                                    readOnly
                                                />
                                            </div>
                                        </div>
                                        <div className="col-6">
                                            <div className="form-group">
                                                <label>Case</label>
                                                <input
                                                    className="form-control"
                                                    placeholder="Case"
                                                    value={patientDetails?.caseId?.name}
                                                    readOnly
                                                />
                                            </div>
                                        </div>
                                        <div className="col-6">
                                            <div className="form-group">
                                                <label>Consultant Doctor</label>
                                                <input
                                                    className="form-control"
                                                    placeholder="Consultant Doctor"
                                                    value={patientDetails?.consultantDoctorId?.name}
                                                    readOnly
                                                />
                                            </div>
                                        </div>
                                        <div className="col-6">
                                            <div className="form-group">
                                                <label>ReferenceDoctor</label>
                                                <input
                                                    className="form-control"
                                                    placeholder="ReferenceDoctor"
                                                    value={patientDetails?.referenceDoctorId?.name}
                                                    readOnly
                                                />
                                            </div>
                                        </div>
                                        <div className="col-6">
                                            <div className="form-group">
                                                <label>Address</label>
                                                <input
                                                    className="form-control"
                                                    placeholder="Address"
                                                    value={patientDetails?.patientId?.address}
                                                    readOnly
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* ================= DELIVERY DETAILS ================= */}
                                    <div className="row mt-3">
                                        <div className="col-12 mt-3">
                                            <label>Nature Of Injury</label>
                                            <input className="form-control" {...register("natureOfInjury")} placeholder="Nature Of Injury" />
                                            {errors.natureOfInjury && <span className="text-danger">{errors.natureOfInjury.message}</span>}
                                        </div>
                                        <div className="col-12 mt-3">
                                            <label>Diagnosis</label>
                                            <input className="form-control" {...register("diagnosis")} placeholder="Diagnosis" />
                                            {errors.diagnosis && <span className="text-danger">{errors.diagnosis.message}</span>}
                                        </div>
                                        <div className="col-12 mt-3">
                                            <label>History And Physical Examination</label>
                                            <textarea className="form-control" {...register("historyAndPhysicalExamination")} rows={5} placeholder="History And Physical Examination" />
                                            {errors.historyAndPhysicalExamination && <span className="text-danger">{errors.historyAndPhysicalExamination.message}</span>}
                                        </div>
                                        <div className="col-12 mt-3">
                                            <label>Treatment</label>
                                            <input className="form-control" {...register("treatment")} placeholder="Treatment" />
                                            {errors.treatment && <span className="text-danger">{errors.treatment.message}</span>}
                                        </div>
                                        <div className="col-12 mt-3">
                                            <label>Mark Of Identification</label>
                                            <input className="form-control" {...register("markOfIdentification")} placeholder="Mark Of Identification" />
                                            {errors.markOfIdentification && <span className="text-danger">{errors.markOfIdentification.message}</span>}
                                        </div>
                                        <div className="col-12 mt-3">
                                            <label>Remark</label>
                                            <textarea className="form-control" {...register("remark")} rows={5} placeholder="Remark" />
                                            {errors.remark && <span className="text-danger">{errors.remark.message}</span>}
                                        </div>
                                        <div className="col-12 mt-3">
                                            <label>Thana Name</label>
                                            <input className="form-control" {...register("thanaName")} placeholder="Thana Name" />
                                            {errors.thanaName && <span className="text-danger">{errors.thanaName.message}</span>}
                                        </div>
                                        <div className="col-12 mt-3">
                                            <label>Patient Bought By</label>
                                            <input className="form-control" {...register("patientBoughtBy")} placeholder="Patient Bought By" />
                                            {errors.patientBoughtBy && <span className="text-danger">{errors.patientBoughtBy.message}</span>}
                                        </div>
                                        <div className="col-12 mt-3">
                                            <label>Place Of Accident</label>
                                            <input className="form-control" {...register("placeOfAccident")} placeholder="Place Of Accident" />
                                            {errors.placeOfAccident && <span className="text-danger">{errors.placeOfAccident.message}</span>}
                                        </div>
                                    </div>
                                </div>

                                <div className="card-footer text-end">
                                    <button type="submit" className="btn btn-primary">
                                        Save
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </Fragment>
    );
};

export default EditMlcEntry;
